import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { 
  Users, 
  Phone, 
  Mail, 
  Clock, 
  Loader2,
  MessageSquare,
  CheckCircle,
  AlertCircle,
  Car,
  RefreshCw,
  Send,
  Bot,
  User,
  Facebook,
  Instagram,
  MessageCircle,
  ToggleLeft,
  ToggleRight,
  ChevronDown,
  ChevronUp,
  Sparkles,
  X,
  ExternalLink,
  Bell,
  Wifi,
  WifiOff
} from "lucide-react";
import { toast } from "sonner";
import { useBusiness } from "@/contexts/BusinessContext";
import { usePushNotifications } from "@/hooks/usePushNotifications";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LeadsTab() {
  const { currentBusiness, businessId } = useBusiness();
  const { permission, notifyNewLead, requestPermission } = usePushNotifications();
  const [activeView, setActiveView] = useState("conversations"); // "conversations" | "form_leads"
  const [conversations, setConversations] = useState([]);
  const [formLeads, setFormLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [generatingResponse, setGeneratingResponse] = useState(false);
  const [sendingResponse, setSendingResponse] = useState(false);
  const [updatingLead, setUpdatingLead] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [platformFilter, setPlatformFilter] = useState("all");
  const [sseConnected, setSseConnected] = useState(false);
  const eventSourceRef = useRef(null);

  // Setup SSE connection for real-time notifications
  useEffect(() => {
    const connectSSE = () => {
      const eventSource = new EventSource(`${API}/leads/notifications/stream`);
      eventSourceRef.current = eventSource;

      eventSource.onopen = () => {
        console.log('SSE connected');
        setSseConnected(true);
      };

      eventSource.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          
          if (data.type === 'new_lead') {
            // Check if this lead is for the current business
            if (data.business_id === businessId) {
              // Show browser notification
              if (permission === 'granted') {
                notifyNewLead({
                  senderName: data.sender_name,
                  platform: data.platform,
                  message: data.message,
                  businessName: currentBusiness?.name
                });
              }
              
              // Show toast notification
              toast.success(`Nuevo lead de ${data.sender_name}`, {
                description: data.message?.substring(0, 50) + '...',
                duration: 10000
              });
              
              // Refresh the conversations list
              fetchData();
            }
          }
        } catch (error) {
          // Ignore parse errors for heartbeat messages
          if (event.data !== 'heartbeat') {
            console.error('Error parsing SSE message:', error);
          }
        }
      };

      eventSource.onerror = () => {
        console.log('SSE disconnected, reconnecting...');
        setSseConnected(false);
        eventSource.close();
        // Reconnect after 5 seconds
        setTimeout(connectSSE, 5000);
      };
    };

    connectSSE();

    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }
    };
  }, [businessId, permission, notifyNewLead, currentBusiness?.name]);

  // Fetch data based on active view
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      if (activeView === "conversations") {
        const response = await axios.get(`${API}/leads/conversations?business_id=${businessId}`);
        if (response.data.success) {
          setConversations(response.data.conversations || []);
        }
      } else {
        const response = await axios.get(`${API}/leads?business_id=${businessId}`);
        if (response.data.success) {
          setFormLeads(response.data.leads || []);
        }
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Error al cargar los datos");
    } finally {
      setLoading(false);
    }
  }, [businessId, activeView]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Generate AI response
  const generateAIResponse = async (conversationId, lastMessage) => {
    setGeneratingResponse(true);
    try {
      const response = await axios.post(`${API}/leads/conversations/${conversationId}/generate-response`, {
        conversation_id: conversationId,
        message: lastMessage,
        auto_send: false
      });
      if (response.data.success) {
        setReplyText(response.data.response);
        toast.success("Respuesta generada con IA");
        // Refresh conversation to get updated suggested_response
        await fetchConversationDetails(conversationId);
      }
    } catch (error) {
      console.error("Error generating response:", error);
      toast.error("Error al generar respuesta con IA");
    } finally {
      setGeneratingResponse(false);
    }
  };

  // Fetch single conversation details
  const fetchConversationDetails = async (convId) => {
    try {
      const response = await axios.get(`${API}/leads/conversations/${convId}`);
      if (response.data.success) {
        setSelectedConversation(response.data.conversation);
        // Update in list
        setConversations(prev => prev.map(c => 
          c.id === convId ? response.data.conversation : c
        ));
      }
    } catch (error) {
      console.error("Error fetching conversation:", error);
    }
  };

  // Send response manually
  const sendResponse = async (conversationId) => {
    if (!replyText.trim()) {
      toast.error("Escribe una respuesta");
      return;
    }
    setSendingResponse(true);
    try {
      const response = await axios.post(
        `${API}/leads/conversations/${conversationId}/send?message=${encodeURIComponent(replyText)}`
      );
      if (response.data.success) {
        toast.success("Respuesta enviada");
        setReplyText("");
        await fetchConversationDetails(conversationId);
      }
    } catch (error) {
      console.error("Error sending response:", error);
      toast.error("Error al enviar respuesta");
    } finally {
      setSendingResponse(false);
    }
  };

  // Toggle auto-reply
  const toggleAutoReply = async (conversationId, currentValue) => {
    try {
      const response = await axios.patch(
        `${API}/leads/conversations/${conversationId}/auto-reply?enabled=${!currentValue}`
      );
      if (response.data.success) {
        toast.success(`Auto-respuesta ${!currentValue ? 'activada' : 'desactivada'}`);
        await fetchConversationDetails(conversationId);
      }
    } catch (error) {
      console.error("Error toggling auto-reply:", error);
      toast.error("Error al cambiar configuración");
    }
  };

  // Update conversation status
  const updateConversationStatus = async (convId, newStatus) => {
    try {
      const response = await axios.patch(
        `${API}/leads/conversations/${convId}/status?status=${newStatus}`
      );
      if (response.data.success) {
        toast.success(`Estado actualizado a "${newStatus}"`);
        await fetchData();
        if (selectedConversation?.id === convId) {
          await fetchConversationDetails(convId);
        }
      }
    } catch (error) {
      console.error("Error updating status:", error);
      toast.error("Error al actualizar estado");
    }
  };

  // Update form lead status
  const updateLeadStatus = async (leadId, newStatus) => {
    setUpdatingLead(leadId);
    try {
      const response = await axios.put(`${API}/leads/${leadId}/status`, { 
        status: newStatus,
        business_id: businessId 
      });
      if (response.data.success) {
        setFormLeads(formLeads.map(lead => 
          lead.id === leadId ? { ...lead, status: newStatus } : lead
        ));
        toast.success(`Lead marcado como "${newStatus}"`);
      }
    } catch (error) {
      console.error("Error updating lead:", error);
      toast.error("Error al actualizar el lead");
    } finally {
      setUpdatingLead(null);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("es-MX", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  const getPlatformIcon = (platform) => {
    switch (platform) {
      case "facebook_messenger":
      case "facebook_comment":
        return <Facebook className="w-4 h-4 text-blue-500" />;
      case "instagram_dm":
      case "instagram_comment":
        return <Instagram className="w-4 h-4 text-pink-500" />;
      case "whatsapp":
        return <MessageCircle className="w-4 h-4 text-green-500" />;
      default:
        return <MessageSquare className="w-4 h-4 text-slate-500" />;
    }
  };

  const getPlatformLabel = (platform) => {
    const labels = {
      "facebook_messenger": "Facebook Messenger",
      "facebook_comment": "Comentario Facebook",
      "instagram_dm": "Instagram DM",
      "instagram_comment": "Comentario Instagram",
      "whatsapp": "WhatsApp"
    };
    return labels[platform] || platform;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "nuevo":
        return "bg-green-500/20 text-green-400 border-green-500/50";
      case "en_proceso":
        return "bg-orange-500/20 text-orange-400 border-orange-500/50";
      case "respondido":
        return "bg-blue-500/20 text-blue-400 border-blue-500/50";
      case "cerrado":
        return "bg-purple-500/20 text-purple-400 border-purple-500/50";
      case "contactado":
        return "bg-blue-500/20 text-blue-400 border-blue-500/50";
      case "no_interesado":
        return "bg-slate-500/20 text-slate-400 border-slate-500/50";
      default:
        return "bg-slate-700 text-slate-400 border-slate-600";
    }
  };

  const conversationStatusOptions = [
    { value: "nuevo", label: "Nuevo" },
    { value: "en_proceso", label: "En Proceso" },
    { value: "respondido", label: "Respondido" },
    { value: "cerrado", label: "Cerrado" }
  ];

  const leadStatusOptions = [
    { value: "nuevo", label: "Nuevo", icon: AlertCircle },
    { value: "contactado", label: "Contactado", icon: Phone },
    { value: "en_proceso", label: "En Proceso", icon: Clock },
    { value: "cerrado", label: "Cerrado", icon: CheckCircle },
    { value: "no_interesado", label: "No Interesado", icon: Users }
  ];

  // Filter conversations
  const filteredConversations = conversations.filter(conv => {
    if (statusFilter !== "all" && conv.status !== statusFilter) return false;
    if (platformFilter !== "all" && conv.platform !== platformFilter) return false;
    return true;
  });

  // Stats for conversations
  const conversationStats = {
    total: conversations.length,
    nuevo: conversations.filter(c => c.status === "nuevo").length,
    en_proceso: conversations.filter(c => c.status === "en_proceso").length,
    respondido: conversations.filter(c => c.status === "respondido").length,
    cerrado: conversations.filter(c => c.status === "cerrado").length,
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <h2 className="text-xl font-bold text-white flex items-center gap-3">
            <Users className="w-6 h-6" style={{ color: currentBusiness?.accent_color || '#3B82F6' }} />
            Centro de Leads - {currentBusiness?.name || 'HEBEH'}
          </h2>
          {/* Real-time connection indicator */}
          <div 
            className={`flex items-center gap-1.5 px-2 py-1 rounded-full text-xs ${
              sseConnected 
                ? 'bg-green-500/20 text-green-400' 
                : 'bg-yellow-500/20 text-yellow-400'
            }`}
            title={sseConnected ? 'Conectado - Notificaciones en tiempo real activas' : 'Reconectando...'}
          >
            {sseConnected ? <Wifi className="w-3 h-3" /> : <WifiOff className="w-3 h-3" />}
            {sseConnected ? 'En vivo' : 'Reconectando'}
          </div>
          {/* Notification permission indicator */}
          {permission !== 'granted' && (
            <button
              onClick={requestPermission}
              className="flex items-center gap-1.5 px-2 py-1 rounded-full text-xs bg-blue-500/20 text-blue-400 hover:bg-blue-500/30 transition-colors"
              title="Activar notificaciones del navegador"
            >
              <Bell className="w-3 h-3" />
              Activar alertas
            </button>
          )}
        </div>
        
        {/* View Toggle */}
        <div className="flex items-center gap-2">
          <div className="flex bg-slate-800 rounded-lg p-1">
            <button
              onClick={() => setActiveView("conversations")}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                activeView === "conversations" 
                  ? "bg-slate-700 text-white" 
                  : "text-slate-400 hover:text-white"
              }`}
              data-testid="view-conversations-btn"
            >
              <div className="flex items-center gap-2">
                <MessageSquare className="w-4 h-4" />
                Conversaciones
              </div>
            </button>
            <button
              onClick={() => setActiveView("form_leads")}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                activeView === "form_leads" 
                  ? "bg-slate-700 text-white" 
                  : "text-slate-400 hover:text-white"
              }`}
              data-testid="view-form-leads-btn"
            >
              <div className="flex items-center gap-2">
                <Mail className="w-4 h-4" />
                Formulario
              </div>
            </button>
          </div>
          
          <button
            onClick={fetchData}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-800 text-slate-300 hover:bg-slate-700 transition-colors"
            data-testid="refresh-leads-btn"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Conversations View */}
      {activeView === "conversations" && (
        <>
          {/* Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div 
              onClick={() => setStatusFilter("all")}
              className={`cursor-pointer glass-card rounded-lg p-4 border transition-all ${
                statusFilter === "all" ? "border-white/50 ring-2 ring-white/20" : "border-slate-700 hover:border-slate-600"
              }`}
            >
              <div className="flex items-center gap-2 mb-1 text-slate-400">
                <MessageSquare className="w-4 h-4" />
                <span className="text-sm font-medium">Total</span>
              </div>
              <p className="text-2xl font-bold text-white">{conversationStats.total}</p>
            </div>
            {conversationStatusOptions.map((option) => {
              const count = conversationStats[option.value] || 0;
              return (
                <div 
                  key={option.value}
                  onClick={() => setStatusFilter(statusFilter === option.value ? "all" : option.value)}
                  className={`cursor-pointer glass-card rounded-lg p-4 border transition-all ${
                    statusFilter === option.value ? "border-white/50 ring-2 ring-white/20" : "border-slate-700 hover:border-slate-600"
                  } ${getStatusColor(option.value)}`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-sm font-medium">{option.label}</span>
                  </div>
                  <p className="text-2xl font-bold">{count}</p>
                </div>
              );
            })}
          </div>

          {/* Platform Filter */}
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-slate-400 text-sm">Plataforma:</span>
            {[
              { value: "all", label: "Todas", icon: MessageSquare },
              { value: "facebook_messenger", label: "Messenger", icon: Facebook },
              { value: "facebook_comment", label: "FB Comments", icon: Facebook },
              { value: "instagram_dm", label: "IG DM", icon: Instagram },
              { value: "instagram_comment", label: "IG Comments", icon: Instagram },
            ].map((p) => {
              const Icon = p.icon;
              return (
                <button
                  key={p.value}
                  onClick={() => setPlatformFilter(p.value)}
                  className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm transition-colors ${
                    platformFilter === p.value
                      ? "bg-slate-700 text-white"
                      : "bg-slate-800/50 text-slate-400 hover:bg-slate-700/50"
                  }`}
                >
                  <Icon className="w-3 h-3" />
                  {p.label}
                </button>
              );
            })}
          </div>

          {/* Conversations List + Detail */}
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
            </div>
          ) : filteredConversations.length > 0 ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Conversations List */}
              <div className="space-y-3 max-h-[600px] overflow-y-auto pr-2">
                {filteredConversations.map((conv) => (
                  <ConversationCard
                    key={conv.id}
                    conversation={conv}
                    isSelected={selectedConversation?.id === conv.id}
                    onSelect={() => {
                      setSelectedConversation(conv);
                      if (conv.suggested_response) {
                        setReplyText(conv.suggested_response);
                      } else {
                        setReplyText("");
                      }
                    }}
                    getPlatformIcon={getPlatformIcon}
                    getPlatformLabel={getPlatformLabel}
                    getStatusColor={getStatusColor}
                    formatDate={formatDate}
                    accentColor={currentBusiness?.accent_color}
                  />
                ))}
              </div>

              {/* Conversation Detail */}
              {selectedConversation ? (
                <ConversationDetail
                  conversation={selectedConversation}
                  replyText={replyText}
                  setReplyText={setReplyText}
                  onGenerateAI={() => {
                    const lastUserMsg = [...selectedConversation.messages].reverse().find(m => m.role === "user");
                    generateAIResponse(selectedConversation.id, lastUserMsg?.content || "");
                  }}
                  onSendResponse={() => sendResponse(selectedConversation.id)}
                  onToggleAutoReply={() => toggleAutoReply(selectedConversation.id, selectedConversation.auto_reply_enabled)}
                  onUpdateStatus={(status) => updateConversationStatus(selectedConversation.id, status)}
                  onClose={() => setSelectedConversation(null)}
                  generatingResponse={generatingResponse}
                  sendingResponse={sendingResponse}
                  getPlatformIcon={getPlatformIcon}
                  getPlatformLabel={getPlatformLabel}
                  getStatusColor={getStatusColor}
                  statusOptions={conversationStatusOptions}
                  formatDate={formatDate}
                  accentColor={currentBusiness?.accent_color}
                />
              ) : (
                <div className="glass-card rounded-lg p-12 text-center border border-slate-700">
                  <MessageSquare className="w-16 h-16 mx-auto text-slate-600 mb-4" />
                  <h3 className="text-xl font-semibold text-white mb-2">Selecciona una conversación</h3>
                  <p className="text-slate-400">
                    Haz clic en una conversación de la lista para ver los detalles y responder.
                  </p>
                </div>
              )}
            </div>
          ) : (
            <EmptyConversations currentBusiness={currentBusiness} />
          )}
        </>
      )}

      {/* Form Leads View (Original) */}
      {activeView === "form_leads" && (
        <FormLeadsView
          leads={formLeads}
          loading={loading}
          statusOptions={leadStatusOptions}
          getStatusColor={getStatusColor}
          formatDate={formatDate}
          updateLeadStatus={updateLeadStatus}
          updatingLead={updatingLead}
          currentBusiness={currentBusiness}
        />
      )}
    </div>
  );
}

// Conversation Card Component
function ConversationCard({ 
  conversation, 
  isSelected, 
  onSelect, 
  getPlatformIcon, 
  getPlatformLabel,
  getStatusColor, 
  formatDate,
  accentColor 
}) {
  const lastMessage = conversation.messages?.[conversation.messages.length - 1];
  const isNew = conversation.status === "nuevo";
  
  return (
    <div
      onClick={onSelect}
      className={`glass-card rounded-lg p-4 cursor-pointer transition-all border ${
        isSelected 
          ? "border-blue-500 ring-2 ring-blue-500/20" 
          : isNew 
            ? "border-green-500/50 hover:border-green-400" 
            : "border-slate-700 hover:border-slate-600"
      }`}
      data-testid={`conversation-card-${conversation.id}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          {getPlatformIcon(conversation.platform)}
          <span className="text-white font-medium truncate max-w-[150px]">
            {conversation.sender_name || `Usuario ${conversation.sender_id?.slice(-6)}`}
          </span>
        </div>
        <span className={`px-2 py-0.5 rounded text-xs font-medium border ${getStatusColor(conversation.status)}`}>
          {conversation.status || "nuevo"}
        </span>
      </div>
      
      <p className="text-slate-400 text-sm mt-2 truncate">
        {getPlatformLabel(conversation.platform)}
      </p>
      
      {lastMessage && (
        <div className="mt-3 bg-slate-800/50 rounded p-2">
          <div className="flex items-center gap-1 text-xs text-slate-500 mb-1">
            {lastMessage.role === "user" ? (
              <User className="w-3 h-3" />
            ) : (
              <Bot className="w-3 h-3" />
            )}
            {lastMessage.role === "user" ? "Cliente" : "Asistente"}
          </div>
          <p className="text-slate-300 text-sm line-clamp-2">{lastMessage.content}</p>
        </div>
      )}
      
      <div className="flex items-center justify-between mt-3">
        <span className="text-slate-500 text-xs">{formatDate(conversation.updated_at)}</span>
        <div className="flex items-center gap-1">
          {conversation.auto_reply_enabled ? (
            <span className="flex items-center gap-1 text-xs text-green-400">
              <Bot className="w-3 h-3" />
              Auto
            </span>
          ) : (
            <span className="flex items-center gap-1 text-xs text-slate-500">
              <User className="w-3 h-3" />
              Manual
            </span>
          )}
        </div>
      </div>
      
      {conversation.messages?.length > 1 && (
        <div className="mt-2 text-xs text-slate-500">
          {conversation.messages.length} mensajes
        </div>
      )}
    </div>
  );
}

// Conversation Detail Component
function ConversationDetail({
  conversation,
  replyText,
  setReplyText,
  onGenerateAI,
  onSendResponse,
  onToggleAutoReply,
  onUpdateStatus,
  onClose,
  generatingResponse,
  sendingResponse,
  getPlatformIcon,
  getPlatformLabel,
  getStatusColor,
  statusOptions,
  formatDate,
  accentColor
}) {
  const [showStatusMenu, setShowStatusMenu] = useState(false);
  
  return (
    <div className="glass-card rounded-lg border border-slate-700 flex flex-col max-h-[600px]">
      {/* Header */}
      <div className="p-4 border-b border-slate-700 flex items-center justify-between">
        <div className="flex items-center gap-3">
          {getPlatformIcon(conversation.platform)}
          <div>
            <h3 className="text-white font-semibold">
              {conversation.sender_name || `Usuario ${conversation.sender_id?.slice(-6)}`}
            </h3>
            <p className="text-slate-400 text-sm">{getPlatformLabel(conversation.platform)}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* Auto-reply toggle */}
          <button
            onClick={onToggleAutoReply}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm transition-colors ${
              conversation.auto_reply_enabled
                ? "bg-green-500/20 text-green-400"
                : "bg-slate-700 text-slate-400"
            }`}
            title={conversation.auto_reply_enabled ? "Desactivar auto-respuesta" : "Activar auto-respuesta"}
          >
            {conversation.auto_reply_enabled ? (
              <ToggleRight className="w-4 h-4" />
            ) : (
              <ToggleLeft className="w-4 h-4" />
            )}
            Auto IA
          </button>
          
          {/* Status dropdown */}
          <div className="relative">
            <button
              onClick={() => setShowStatusMenu(!showStatusMenu)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium border flex items-center gap-2 ${getStatusColor(conversation.status)}`}
            >
              {conversation.status || "nuevo"}
              <ChevronDown className="w-3 h-3" />
            </button>
            {showStatusMenu && (
              <div className="absolute right-0 top-full mt-1 w-40 bg-slate-800 border border-slate-700 rounded-lg shadow-xl z-10">
                {statusOptions.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => {
                      onUpdateStatus(option.value);
                      setShowStatusMenu(false);
                    }}
                    className={`w-full px-4 py-2 text-left text-sm hover:bg-slate-700 transition-colors first:rounded-t-lg last:rounded-b-lg ${
                      conversation.status === option.value ? 'text-blue-400' : 'text-slate-300'
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            )}
          </div>
          
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-slate-700 text-slate-400"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
      
      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {conversation.messages?.map((msg, idx) => (
          <div
            key={idx}
            className={`flex ${msg.role === "assistant" ? "justify-end" : "justify-start"}`}
          >
            <div
              className={`max-w-[80%] rounded-lg p-3 ${
                msg.role === "assistant"
                  ? "bg-blue-500/20 text-blue-100"
                  : "bg-slate-700 text-slate-200"
              }`}
            >
              <div className="flex items-center gap-2 mb-1 text-xs opacity-70">
                {msg.role === "user" ? (
                  <>
                    <User className="w-3 h-3" />
                    Cliente
                  </>
                ) : (
                  <>
                    <Bot className="w-3 h-3" />
                    Asistente {msg.sent && "(enviado)"}
                  </>
                )}
              </div>
              <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
              <p className="text-xs opacity-50 mt-1">
                {formatDate(msg.timestamp)}
              </p>
            </div>
          </div>
        ))}
        
        {/* Suggested response indicator */}
        {conversation.suggested_response && !conversation.messages?.some(m => m.content === conversation.suggested_response && m.sent) && (
          <div className="flex justify-end">
            <div className="max-w-[80%] rounded-lg p-3 bg-yellow-500/10 border border-yellow-500/30 text-yellow-200">
              <div className="flex items-center gap-2 mb-1 text-xs text-yellow-400">
                <Sparkles className="w-3 h-3" />
                Respuesta sugerida (pendiente de aprobación)
              </div>
              <p className="text-sm whitespace-pre-wrap">{conversation.suggested_response}</p>
            </div>
          </div>
        )}
      </div>
      
      {/* Reply Input */}
      <div className="p-4 border-t border-slate-700">
        <div className="flex flex-col gap-3">
          <textarea
            value={replyText}
            onChange={(e) => setReplyText(e.target.value)}
            placeholder="Escribe tu respuesta o genera una con IA..."
            className="w-full bg-slate-800 border border-slate-700 rounded-lg p-3 text-white placeholder-slate-500 resize-none focus:outline-none focus:border-blue-500"
            rows={3}
          />
          <div className="flex items-center justify-between gap-2">
            <button
              onClick={onGenerateAI}
              disabled={generatingResponse}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-purple-500/20 text-purple-400 hover:bg-purple-500/30 transition-colors disabled:opacity-50"
            >
              {generatingResponse ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Sparkles className="w-4 h-4" />
              )}
              Generar con IA
            </button>
            <button
              onClick={onSendResponse}
              disabled={sendingResponse || !replyText.trim()}
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-white transition-colors disabled:opacity-50"
              style={{ backgroundColor: accentColor || '#3B82F6' }}
            >
              {sendingResponse ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Send className="w-4 h-4" />
              )}
              Enviar Respuesta
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Empty Conversations Component
function EmptyConversations({ currentBusiness }) {
  return (
    <div className="glass-card rounded-lg p-12 text-center border border-slate-700">
      <MessageSquare className="w-16 h-16 mx-auto text-slate-600 mb-4" />
      <h3 className="text-xl font-semibold text-white mb-2">No hay conversaciones todavía</h3>
      <p className="text-slate-400 max-w-lg mx-auto mb-6">
        Las conversaciones de Facebook Messenger, comentarios e Instagram aparecerán aquí 
        una vez que configures los webhooks de Meta.
      </p>
      <div className="bg-slate-800/50 rounded-lg p-4 text-left max-w-md mx-auto">
        <h4 className="text-white font-medium mb-2 flex items-center gap-2">
          <ExternalLink className="w-4 h-4" />
          Pasos para configurar Webhooks:
        </h4>
        <ol className="text-slate-400 text-sm space-y-2 list-decimal list-inside">
          <li>Ve a tu App en Meta Developer Portal</li>
          <li>Selecciona "Webhooks" en el menú lateral</li>
          <li>Agrega el endpoint de tu aplicación</li>
          <li>Suscríbete a los eventos de mensajes y comentarios</li>
        </ol>
      </div>
    </div>
  );
}

// Form Leads View Component
function FormLeadsView({ 
  leads, 
  loading, 
  statusOptions, 
  getStatusColor, 
  formatDate, 
  updateLeadStatus, 
  updatingLead,
  currentBusiness 
}) {
  const newLeads = leads.filter(l => l.status === "nuevo" || !l.status);
  const otherLeads = leads.filter(l => l.status && l.status !== "nuevo");

  // Stats
  const stats = statusOptions.map(option => ({
    ...option,
    count: leads.filter(l => (l.status || "nuevo") === option.value).length
  }));

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
      </div>
    );
  }

  return (
    <>
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {stats.map((option) => {
          const Icon = option.icon;
          return (
            <div 
              key={option.value}
              className={`glass-card rounded-lg p-4 border ${getStatusColor(option.value)}`}
            >
              <div className="flex items-center gap-2 mb-1">
                <Icon className="w-4 h-4" />
                <span className="text-sm font-medium">{option.label}</span>
              </div>
              <p className="text-2xl font-bold">{option.count}</p>
            </div>
          );
        })}
      </div>

      {leads.length > 0 ? (
        <div className="space-y-6">
          {/* New Leads Section */}
          {newLeads.length > 0 && (
            <div>
              <h3 className="text-lg font-semibold text-green-400 mb-4 flex items-center gap-2">
                <AlertCircle className="w-5 h-5" />
                Leads Nuevos ({newLeads.length})
              </h3>
              <div className="grid gap-4">
                {newLeads.map((lead) => (
                  <FormLeadCard 
                    key={lead.id} 
                    lead={lead} 
                    statusOptions={statusOptions}
                    getStatusColor={getStatusColor}
                    formatDate={formatDate}
                    updateLeadStatus={updateLeadStatus}
                    updatingLead={updatingLead}
                    accentColor={currentBusiness?.accent_color}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Other Leads Section */}
          {otherLeads.length > 0 && (
            <div>
              <h3 className="text-lg font-semibold text-slate-400 mb-4 flex items-center gap-2">
                <Users className="w-5 h-5" />
                Otros Leads ({otherLeads.length})
              </h3>
              <div className="grid gap-4">
                {otherLeads.map((lead) => (
                  <FormLeadCard 
                    key={lead.id} 
                    lead={lead} 
                    statusOptions={statusOptions}
                    getStatusColor={getStatusColor}
                    formatDate={formatDate}
                    updateLeadStatus={updateLeadStatus}
                    updatingLead={updatingLead}
                    accentColor={currentBusiness?.accent_color}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="glass-card rounded-lg p-12 text-center">
          <Users className="w-16 h-16 mx-auto text-slate-600 mb-4" />
          <h3 className="text-xl font-semibold text-white mb-2">No hay leads del formulario</h3>
          <p className="text-slate-400 max-w-md mx-auto">
            Los leads aparecerán aquí cuando los usuarios llenen el formulario de contacto 
            en el catálogo público de {currentBusiness?.name || 'tu negocio'}.
          </p>
        </div>
      )}
    </>
  );
}

// Form Lead Card Component
function FormLeadCard({ lead, statusOptions, getStatusColor, formatDate, updateLeadStatus, updatingLead, accentColor }) {
  const [showStatusMenu, setShowStatusMenu] = useState(false);

  return (
    <div 
      className="glass-card rounded-lg p-5 border border-slate-700 hover:border-slate-600 transition-colors"
      data-testid={`lead-card-${lead.id}`}
    >
      <div className="flex flex-col md:flex-row md:items-start gap-4">
        {/* Lead Info */}
        <div className="flex-1 space-y-3">
          <div className="flex items-start justify-between">
            <div>
              <h4 className="text-lg font-semibold text-white">{lead.name}</h4>
              <p className="text-slate-500 text-sm flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {formatDate(lead.created_at)}
              </p>
            </div>
            
            {/* Status Badge & Dropdown */}
            <div className="relative">
              <button
                onClick={() => setShowStatusMenu(!showStatusMenu)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium border flex items-center gap-2 transition-colors ${getStatusColor(lead.status || "nuevo")}`}
                disabled={updatingLead === lead.id}
              >
                {updatingLead === lead.id ? (
                  <Loader2 className="w-3 h-3 animate-spin" />
                ) : null}
                {statusOptions.find(s => s.value === (lead.status || "nuevo"))?.label || "Nuevo"}
              </button>
              
              {showStatusMenu && (
                <div className="absolute right-0 top-full mt-1 w-48 bg-slate-800 border border-slate-700 rounded-lg shadow-xl z-10">
                  {statusOptions.map((option) => {
                    const Icon = option.icon;
                    return (
                      <button
                        key={option.value}
                        onClick={() => {
                          updateLeadStatus(lead.id, option.value);
                          setShowStatusMenu(false);
                        }}
                        className={`w-full px-4 py-2 text-left text-sm flex items-center gap-2 hover:bg-slate-700 transition-colors first:rounded-t-lg last:rounded-b-lg ${
                          (lead.status || "nuevo") === option.value ? 'text-blue-400' : 'text-slate-300'
                        }`}
                      >
                        <Icon className="w-4 h-4" />
                        {option.label}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Contact Info */}
          <div className="flex flex-wrap gap-4">
            <a 
              href={`tel:${lead.phone}`}
              className="flex items-center gap-2 text-slate-300 hover:text-white transition-colors"
            >
              <Phone className="w-4 h-4" style={{ color: accentColor || '#3B82F6' }} />
              {lead.phone}
            </a>
            {lead.email && (
              <a 
                href={`mailto:${lead.email}`}
                className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors"
              >
                <Mail className="w-4 h-4" />
                {lead.email}
              </a>
            )}
          </div>

          {/* Vehicle Interest */}
          {lead.vehicle_name && (
            <div className="flex items-center gap-2 text-slate-400">
              <Car className="w-4 h-4" />
              <span>Interesado en: <span className="text-white">{lead.vehicle_name}</span></span>
            </div>
          )}

          {/* Message */}
          {lead.message && (
            <div className="bg-slate-800/50 rounded-lg p-3">
              <div className="flex items-start gap-2">
                <MessageSquare className="w-4 h-4 text-slate-500 mt-0.5 flex-shrink-0" />
                <p className="text-slate-300 text-sm">{lead.message}</p>
              </div>
            </div>
          )}
        </div>

        {/* Quick Actions */}
        <div className="flex md:flex-col gap-2">
          <a
            href={`https://api.whatsapp.com/send?phone=${lead.phone?.replace(/\D/g, '')}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-green-500/20 text-green-400 hover:bg-green-500/30 transition-colors text-sm"
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
            </svg>
            WhatsApp
          </a>
          <a
            href={`tel:${lead.phone}`}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-500/20 text-blue-400 hover:bg-blue-500/30 transition-colors text-sm"
          >
            <Phone className="w-4 h-4" />
            Llamar
          </a>
        </div>
      </div>
    </div>
  );
}
